<script>
  import { createEventDispatcher } from 'svelte';
  import { RotateCcw, Save, SlidersHorizontal, X } from 'lucide-svelte';

  export let open = false;
  export let loading = false;
  export let saving = false;
  export let config = null;
  export let profileOptions = [];
  export let resolutionOptions = [];
  export let frameRateOptions = [];
  export let segmentOptions = [];
  export let mimeOptions = [];
  export let recordingTransportOptions = [];
  export let formatOptions = [];
  export let ffmpegPresetOptions = [];
  export let status = '';
  export let error = '';
  export let dirty = false;

  const dispatch = createEventDispatcher();

  $: selectedFormat = formatOptions.find(({ id }) => id === config?.output?.format);
  $: selectedTransport = recordingTransportOptions.find(({ id }) => id === config?.recording?.transportMode);

  const RECORDING_MODE_PRESETS = {
    'http-chunk': {
      segmentDurationMs: 5000,
      preferredMimeType: 'video/webm;codecs=vp8',
      videoBitsPerSecond: 2500000,
      audioBitsPerSecond: 128000
    },
    'websocket-ffmpeg': {
      segmentDurationMs: 3000,
      preferredMimeType: 'video/webm;codecs=vp8',
      videoBitsPerSecond: 3000000,
      audioBitsPerSecond: 96000,
      outputFormat: 'mp4',
      ffmpegPreset: 'veryfast',
      crf: 23
    },
    webrtc: {
      segmentDurationMs: 2000,
      preferredMimeType: 'video/webm;codecs=vp8,opus',
      videoBitsPerSecond: 1800000,
      audioBitsPerSecond: 96000,
      outputFormat: 'webm',
      ffmpegPreset: 'ultrafast',
      crf: 28
    }
  };

  function applyRecordingTransportMode(mode) {
    config.recording.transportMode = mode;
    const preset = RECORDING_MODE_PRESETS[mode];
    if (preset) {
      config.recording.segmentDurationMs = preset.segmentDurationMs;
      config.recording.preferredMimeType = preset.preferredMimeType;
      config.recording.videoBitsPerSecond = preset.videoBitsPerSecond;
      config.recording.audioBitsPerSecond = preset.audioBitsPerSecond;
      if (preset.outputFormat && config.output) config.output.format = preset.outputFormat;
      if (preset.ffmpegPreset && config.ffmpeg) config.ffmpeg.preset = preset.ffmpegPreset;
      if (typeof preset.crf === 'number' && config.ffmpeg) config.ffmpeg.crf = preset.crf;
    }
    notifyChange();
  }

  function updateProfile(profile) {
    dispatch('profile', { profile });
  }

  function save() {
    dispatch('save');
  }

  function reset() {
    dispatch('reset');
  }

  function close() {
    dispatch('close');
  }

  function stopClick(event) {
    event.stopPropagation();
  }

  function notifyChange() {
    dispatch('change', { config });
  }
</script>

{#if open}
  <div class="panel-backdrop" on:click={close}>
    <aside class="panel-shell" role="dialog" aria-modal="true" aria-label="Cài đặt camera" on:click={stopClick}>
      <header class="panel-header">
        <div class="header-copy">
          <div class="eyebrow"><SlidersHorizontal size="15" /> Backend camera</div>
          <h3>Cấu hình ghi hình</h3>
          <p>
            Tinh chỉnh pipeline camera, recorder và ffmpeg trực tiếp trong màn hình quay với cấu hình lưu trên server.
          </p>
        </div>

        <button
          type="button"
          class="icon-button"
          aria-label="Đóng cài đặt camera"
          title="Đóng cài đặt camera"
          on:click={close}>
          <X size="18" />
        </button>
      </header>

      {#if loading || !config}
        <div class="state-panel">Đang tải cài đặt camera...</div>
      {:else}
        {#if error}
          <div class="message error">{error}</div>
        {/if}
        {#if status}
          <div class="message success">{status}</div>
        {/if}

        <div class="panel-scroll">
          <section class="section">
            <div class="section-title">
              <strong>Hồ sơ vận hành</strong>
              <span>Chọn cấu hình mẫu để bắt đầu nhanh, sau đó tinh chỉnh chi tiết nếu cần.</span>
            </div>
            <label class="field">
              <span>Profile</span>
              <select
                value={config.profile}
                on:change={(event) => {
                  updateProfile(event.currentTarget.value);
                  notifyChange();
                }}>
                {#each profileOptions as option}
                  <option value={option.id}>{option.label}</option>
                {/each}
              </select>
            </label>
          </section>

          <section class="section">
            <div class="section-title">
              <strong>Thiết bị vào</strong>
              <span>Thiết lập camera ưu tiên, độ phân giải, tốc độ khung hình và âm thanh đầu vào.</span>
            </div>

            <div class="grid">
              <label class="field">
                <span>Camera mặc định</span>
                <select bind:value={config.capture.facingMode} on:change={notifyChange}>
                  <option value="environment">Camera sau / ngoài</option>
                  <option value="user">Camera trước</option>
                </select>
              </label>

              <label class="field">
                <span>Độ phân giải</span>
                <select bind:value={config.capture.resolution} on:change={notifyChange}>
                  {#each resolutionOptions as option}
                    <option value={option.id}>{option.label} ({option.width}x{option.height})</option>
                  {/each}
                </select>
              </label>

              <label class="field">
                <span>Tốc độ khung hình</span>
                <select bind:value={config.capture.frameRate} on:change={notifyChange}>
                  {#each frameRateOptions as option}
                    <option value={option}>{option} fps</option>
                  {/each}
                </select>
              </label>

              <label class="field toggle">
                <span>Ghi kèm micro</span>
                <input type="checkbox" bind:checked={config.capture.audioEnabled} on:change={notifyChange} />
              </label>
            </div>
          </section>

          <section class="section">
            <div class="section-title section-title--stacked">
              <div class="section-title__row">
                <strong>Recorder</strong>
                <span>{selectedTransport?.label || 'Ghi hình cơ bản'}</span>
              </div>
              <span>Thiết lập cách ghi phù hợp cho desktop/mobile, sau đó tinh chỉnh định dạng và bitrate theo nhu cầu.</span>
            </div>

            <div class="mode-tabs" role="tablist" aria-label="Chọn chế độ recorder">
              {#each recordingTransportOptions as option}
                <button
                  type="button"
                  class:active={config.recording.transportMode === option.id}
                  class="mode-tab"
                  role="tab"
                  aria-selected={config.recording.transportMode === option.id}
                  on:click={() => applyRecordingTransportMode(option.id)}>
                  <strong>{option.label}</strong>
                  <small>{option.technicalName}</small>
                  <span>{option.description}</span>
                </button>
              {/each}
            </div>

            {#if config.recording.transportMode === 'http-chunk'}
              <div class="hint-card">
                <strong>Ghi hình cơ bản</strong>
                <span>Giữ mọi thứ đơn giản và ổn định. Phần này chỉ cần segment, mime và bitrate.</span>
              </div>
              <div class="grid">
                <label class="field">
                  <span>Thời lượng segment</span>
                  <select bind:value={config.recording.segmentDurationMs} on:change={notifyChange}>
                    {#each segmentOptions as option}
                      <option value={option}>{Math.round(option / 1000)} giây</option>
                    {/each}
                  </select>
                </label>

                <label class="field span-2">
                  <span>Định dạng MediaRecorder</span>
                  <select bind:value={config.recording.preferredMimeType} on:change={notifyChange}>
                    {#each mimeOptions as option}
                      <option value={option.id}>{option.label}</option>
                    {/each}
                  </select>
                </label>

                <label class="field">
                  <span>Video bitrate</span>
                  <input
                    type="number"
                    min="250000"
                    max="20000000"
                    step="250000"
                    bind:value={config.recording.videoBitsPerSecond}
                    on:input={notifyChange} />
                </label>

                <label class="field">
                  <span>Audio bitrate</span>
                  <input
                    type="number"
                    min="32000"
                    max="512000"
                    step="16000"
                    bind:value={config.recording.audioBitsPerSecond}
                    on:input={notifyChange} />
                </label>
              </div>
            {:else if config.recording.transportMode === 'websocket-ffmpeg'}
              <div class="hint-card">
                <strong>Ghi hình trực tiếp</strong>
                <span>Ưu tiên cho server. Phần input giữ tối giản, còn xuất file để FFmpeg lo.</span>
              </div>
              <div class="grid">
                <label class="field">
                  <span>Thời lượng segment</span>
                  <select bind:value={config.recording.segmentDurationMs} on:change={notifyChange}>
                    {#each segmentOptions as option}
                      <option value={option}>{Math.round(option / 1000)} giây</option>
                    {/each}
                  </select>
                </label>

                <label class="field">
                  <span>Video bitrate</span>
                  <input
                    type="number"
                    min="250000"
                    max="20000000"
                    step="250000"
                    bind:value={config.recording.videoBitsPerSecond}
                    on:input={notifyChange} />
                </label>

                <label class="field">
                  <span>Audio bitrate</span>
                  <input
                    type="number"
                    min="32000"
                    max="512000"
                    step="16000"
                    bind:value={config.recording.audioBitsPerSecond}
                    on:input={notifyChange} />
                </label>

                <label class="field span-2">
                  <span>Định dạng đầu ra</span>
                  <select bind:value={config.output.format} on:change={notifyChange}>
                    {#each formatOptions as option}
                      <option value={option.id}>{option.label}</option>
                    {/each}
                  </select>
                  <small>{selectedFormat?.description || ''}</small>
                </label>

                <label class="field">
                  <span>FFmpeg preset</span>
                  <select bind:value={config.ffmpeg.preset} on:change={notifyChange}>
                    {#each ffmpegPresetOptions as option}
                      <option value={option.id}>{option.label}</option>
                    {/each}
                  </select>
                </label>

                <label class="field">
                  <span>CRF</span>
                  <input
                    type="number"
                    min="18"
                    max="35"
                    step="1"
                    bind:value={config.ffmpeg.crf}
                    on:input={notifyChange} />
                </label>
              </div>
            {:else}
              <div class="hint-card">
                <strong>Gọi video realtime</strong>
                <span>Chỉ giữ phần cần thiết nhất: độ trễ thấp, bitrate thấp và mime nhẹ.</span>
              </div>
              <div class="grid">
                <label class="field">
                  <span>Video bitrate</span>
                  <input
                    type="number"
                    min="250000"
                    max="20000000"
                    step="250000"
                    bind:value={config.recording.videoBitsPerSecond}
                    on:input={notifyChange} />
                </label>

                <label class="field">
                  <span>Audio bitrate</span>
                  <input
                    type="number"
                    min="32000"
                    max="512000"
                    step="16000"
                    bind:value={config.recording.audioBitsPerSecond}
                    on:input={notifyChange} />
                </label>

                <label class="field span-2">
                  <span>Định dạng MediaRecorder</span>
                  <select bind:value={config.recording.preferredMimeType} on:change={notifyChange}>
                    {#each mimeOptions as option}
                      <option value={option.id}>{option.label}</option>
                    {/each}
                  </select>
                </label>
              </div>
            {/if}
          </section>

          <section class="section">
            <div class="section-title">
              <strong>Xuất file backend</strong>
              <span>Thiết lập định dạng đầu ra, mức nén ffmpeg và quy tắc đặt tên file sau khi server finalize.</span>
            </div>

            <div class="grid">
              <label class="field">
                <span>Định dạng đầu ra</span>
                <select bind:value={config.output.format} on:change={notifyChange}>
                  {#each formatOptions as option}
                    <option value={option.id}>{option.label}</option>
                  {/each}
                </select>
                <small>{selectedFormat?.description || ''}</small>
              </label>

              <label class="field">
                <span>FFmpeg preset</span>
                <select bind:value={config.ffmpeg.preset} on:change={notifyChange}>
                  {#each ffmpegPresetOptions as option}
                    <option value={option.id}>{option.label}</option>
                  {/each}
                </select>
              </label>

              <label class="field">
                <span>CRF</span>
                <input
                  type="number"
                  min="18"
                  max="35"
                  step="1"
                  bind:value={config.ffmpeg.crf}
                  on:input={notifyChange} />
                <small>Giá trị thấp hơn sẽ ưu tiên chất lượng hình cao hơn.</small>
              </label>

              <label class="field">
                <span>Tên file đầu ra</span>
                <input type="text" maxlength="120" bind:value={config.output.fileNamePattern} on:input={notifyChange} />
                <small>
                  Dùng được <code>{'{date}'}</code>
                  ,
                  <code>{'{time}'}</code>
                  và
                  <code>{'{sessionId}'}</code>
                  .
                </small>
              </label>

              <label class="field toggle">
                <span>Bật faststart</span>
                <input type="checkbox" bind:checked={config.ffmpeg.movFlagsFastStart} on:change={notifyChange} />
              </label>

              <label class="field toggle">
                <span>Tự gửi vào My Cloud</span>
                <input type="checkbox" bind:checked={config.output.shareToCloud} on:change={notifyChange} />
              </label>
            </div>
          </section>

          <section class="section">
            <div class="section-title">
              <strong>Hiển thị</strong>
              <span>Điều khiển lớp gridlines trên preview camera để người dùng căn khung dễ hơn khi quay.</span>
            </div>

            <div class="grid">
              <label class="field toggle span-2">
                <span>Hiện gridlines camera</span>
                <input type="checkbox" bind:checked={config.ui.showGridlines} on:change={notifyChange} />
              </label>
            </div>
          </section>
        </div>

        <footer class="panel-footer">
          <div class="footer-note"></div>

          <div class="footer-actions">
            <button type="button" class="secondary" on:click={reset} disabled={saving}>
              <RotateCcw size="16" />
              <span>Đặt lại</span>
            </button>
            <button type="button" class="primary" on:click={save} disabled={saving || !dirty}>
              <Save size="16" />
              <span>{saving ? 'Đang lưu...' : 'Lưu cấu hình'}</span>
            </button>
          </div>
        </footer>
      {/if}
    </aside>
  </div>
{/if}

<style>
  .panel-backdrop {
    position: absolute;
    inset: 0;
    z-index: 90;
    display: flex;
    align-items: stretch;
    justify-content: flex-end;
    background: rgba(2, 6, 23, 0.48);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }

  .panel-shell {
    width: min(100%, 29rem);
    height: 100%;
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    color: #e2e8f0;
    background: linear-gradient(180deg, rgba(15, 23, 42, 0.96), rgba(15, 23, 42, 0.9));
    border-left: 1px solid rgba(148, 163, 184, 0.24);
    box-shadow: -24px 0 48px rgba(0, 0, 0, 0.28);
  }

  .panel-header,
  .panel-footer,
  .message,
  .state-panel {
    padding: 1rem 1rem 0.9rem;
  }

  .panel-header {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    align-items: flex-start;
  }

  .header-copy {
    display: grid;
    gap: 0.35rem;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    color: #93c5fd;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .header-copy h3 {
    margin: 0;
    color: #f8fafc;
    font-size: 0.98rem;
  }

  .header-copy p,
  .section-title span,
  .field small,
  .footer-note {
    margin: 0;
    color: #94a3b8;
    font-size: 0.76rem;
    line-height: 1.45;
  }

  .icon-button {
    display: grid;
    place-items: center;
    width: 2.4rem;
    height: 2.4rem;
    border-radius: 999px;
    color: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.26);
    background: rgba(30, 41, 59, 0.72);
  }

  .state-panel,
  .message {
    color: #f8fafc;
  }

  .message.error {
    color: #fecaca;
  }

  .message.success {
    color: #bbf7d0;
  }

  .panel-scroll {
    overflow: auto;
    padding: 0 1rem 1rem;
    display: grid;
    gap: 0.9rem;
    scrollbar-width: thin;
    scrollbar-color: rgba(148, 163, 184, 0.45) transparent;
  }

  .panel-scroll::-webkit-scrollbar {
    width: 8px;
  }

  .panel-scroll::-webkit-scrollbar-track {
    background: transparent;
  }

  .panel-scroll::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.42);
    border-radius: 999px;
  }

  .section {
    display: grid;
    gap: 0.85rem;
    padding: 0.95rem;
    border-radius: 8px;
    background: rgba(15, 23, 42, 0.72);
    border: 1px solid rgba(148, 163, 184, 0.16);
  }

  .section-title {
    display: grid;
    gap: 0.25rem;
  }

  .section-title--stacked {
    gap: 0.4rem;
  }

  .section-title__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .section-title strong {
    color: #f8fafc;
    font-size: 0.88rem;
  }

  .section-title__row span {
    color: #bfdbfe;
    font-weight: 700;
    font-size: 0.74rem;
    padding: 0.26rem 0.56rem;
    border-radius: 999px;
    border: 1px solid rgba(96, 165, 250, 0.22);
    background: rgba(37, 99, 235, 0.18);
  }

  .hint-card {
    display: grid;
    gap: 0.15rem;
    padding: 0.75rem 0.8rem;
    border-radius: 10px;
    border: 1px solid rgba(96, 165, 250, 0.16);
    background: rgba(37, 99, 235, 0.12);
  }

  .hint-card strong {
    color: #eff6ff;
    font-size: 0.84rem;
  }

  .hint-card span {
    color: #bfdbfe;
    font-size: 0.75rem;
    line-height: 1.4;
  }

  .mode-tabs {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.6rem;
  }

  .mode-tab {
    text-align: left;
    padding: 0.8rem 0.78rem;
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.22);
    background: rgba(15, 23, 42, 0.76);
    color: #cbd5e1;
    display: grid;
    gap: 0.22rem;
    min-height: 7rem;
    transition: border-color 0.16s ease, background 0.16s ease, transform 0.16s ease, box-shadow 0.16s ease;
  }

  .mode-tab strong {
    color: #f8fafc;
    font-size: 0.86rem;
    line-height: 1.2;
  }

  .mode-tab small {
    color: #93c5fd;
    font-weight: 700;
    font-size: 0.72rem;
  }

  .mode-tab span {
    color: #94a3b8;
    font-size: 0.73rem;
    line-height: 1.35;
  }

  .mode-tab.active {
    border-color: rgba(96, 165, 250, 0.55);
    background: linear-gradient(180deg, rgba(37, 99, 235, 0.26), rgba(15, 23, 42, 0.88));
    box-shadow: 0 10px 22px rgba(37, 99, 235, 0.16);
  }

  .mode-tab:focus-visible {
    outline: 2px solid rgba(96, 165, 250, 0.85);
    outline-offset: 2px;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem;
  }

  .grid > .span-2 {
    grid-column: 1 / -1;
  }

  .field {
    display: grid;
    gap: 0.38rem;
    font-size: 0.78rem;
    color: #cbd5e1;
  }

  .field span {
    font-weight: 600;
  }

  .field input,
  .field select {
    width: 100%;
    min-height: 2.8rem;
    border-radius: 8px;
    border: 1px solid rgba(148, 163, 184, 0.24);
    background: rgba(15, 23, 42, 0.78);
    color: #f8fafc;
    padding: 0 0.8rem;
  }

  .field input[type='checkbox'] {
    width: 1.05rem;
    height: 1.05rem;
    margin: 0;
    padding: 0;
  }

  .field.toggle {
    min-height: 2.8rem;
    border-radius: 8px;
    border: 1px solid rgba(148, 163, 184, 0.24);
    background: rgba(15, 23, 42, 0.78);
    padding: 0.75rem 0.8rem;
    grid-template-columns: 1fr auto;
    align-items: center;
  }

  .field.toggle span {
    line-height: 1.25;
  }

  .panel-footer {
    border-top: 1px solid rgba(148, 163, 184, 0.16);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding-top: 0.95rem;
    background: rgba(2, 6, 23, 0.65);
    position: sticky;
    bottom: 0;
  }

  .footer-actions {
    display: flex;
    gap: 0.6rem;
    flex-shrink: 0;
  }

  .footer-actions button {
    min-height: 2.85rem;
    min-width: 7.5rem;
    padding: 0.5rem 0.9rem;
    border-radius: 8px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    font-weight: 650;
    text-align: center;
    line-height: 1.2;
    white-space: normal;
  }

  .secondary {
    color: #e2e8f0;
    border: 1px solid rgba(148, 163, 184, 0.24);
    background: rgba(30, 41, 59, 0.72);
  }

  .primary {
    color: white;
    border: 1px solid transparent;
    background: #2563eb;
  }

  .footer-actions button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    .panel-backdrop {
      align-items: flex-end;
    }

    .panel-shell {
      width: 100%;
      height: min(82vh, 44rem);
      border-left: none;
      border-top: 1px solid rgba(148, 163, 184, 0.24);
      border-top-left-radius: 18px;
      border-top-right-radius: 18px;
      box-shadow: 0 -24px 48px rgba(0, 0, 0, 0.32);
    }

    .grid {
      grid-template-columns: 1fr;
    }

    .mode-tabs {
      grid-template-columns: 1fr;
    }

    .mode-tab {
      min-height: auto;
    }

    .span-2 {
      grid-column: auto;
    }

    .panel-footer {
      display: grid;
      grid-template-columns: 1fr;
      align-items: stretch;
    }

    .footer-actions {
      width: 100%;
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .footer-actions button {
      min-width: 0;
    }
  }
</style>
